import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useAuth } from '../utils/firebase';

const LaunchSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gameData, setGameData] = useState(null);
  const [sessionId, setSessionId] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      setError('No session ID found');
      setLoading(false);
      return;
    }

    setSessionId(sessionId);
    handleSuccessfulPayment(sessionId);
  }, [searchParams]);

  const handleSuccessfulPayment = async (sessionId) => {
    try {
      // Get the session details from the URL
      const gameId = searchParams.get('gameId');
      const chargeType = searchParams.get('chargeType');

      if (!gameId || !chargeType) {
        throw new Error('Missing game or charge information');
      }

      // Get game data
      const gameDoc = await getDoc(doc(db, 'games', 'content', 'triviaGames', gameId));
      if (!gameDoc.exists()) {
        throw new Error('Game not found');
      }

      const game = { id: gameDoc.id, ...gameDoc.data() };
      setGameData(game);

      // Create launch record
      const launchData = {
        userId: user.uid,
        gameId: gameId,
        gameTitle: game.title,
        chargeType: chargeType,
        stripeSessionId: sessionId,
        launchTime: serverTimestamp(),
        expiresAt: new Date(Date.now() + 12 * 60 * 60 * 1000), // 12 hours
        status: 'active',
        source: chargeType === 'creator_own_10' ? 'user_own' : 'marketplace'
      };

      await addDoc(collection(db, 'launches'), launchData);

      // Copy game to user's games if it's a marketplace game
      if (chargeType !== 'creator_own_10') {
        const userGameData = {
          ...game,
          originalGameId: game.id,
          purchasedFrom: game.creatorId,
          purchasedAt: serverTimestamp(),
          isPurchased: true
        };

        // Remove marketplaceData field
        delete userGameData.marketplaceData;

        // Clean undefined values
        const cleanUserGameData = Object.fromEntries(
          Object.entries(userGameData).filter(([_, value]) => value !== undefined)
        );

        await addDoc(collection(db, 'users', user.uid, 'games'), cleanUserGameData);
      }

      setLoading(false);

    } catch (error) {
      console.error('Error handling successful payment:', error);
      setError(error.message);
      setLoading(false);
    }
  };

  const handleLaunchGame = () => {
    if (gameData) {
      navigate(`/launch/${gameData.id}`);
    }
  };

  const handleGoToMyGames = () => {
    navigate('/my-games');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
          <p className="text-white text-xl">Processing your payment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">❌</div>
          <h1 className="text-2xl font-bold text-white mb-4">Payment Error</h1>
          <p className="text-gray-400 mb-6">{error}</p>
          <button
            onClick={() => navigate('/marketplace')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg"
          >
            Return to Marketplace
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center">
      <div className="max-w-md w-full mx-4">
        <div className="bg-gray-800 rounded-lg p-8 text-center">
          <div className="text-green-500 text-6xl mb-4">✅</div>
          <h1 className="text-2xl font-bold text-white mb-4">Payment Successful!</h1>
          
          {gameData && (
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-white mb-2">{gameData.title}</h2>
              <p className="text-gray-400 mb-4">Your game is ready to launch!</p>
              
              <div className="bg-gray-700 rounded-lg p-4 mb-6">
                <div className="text-sm text-gray-400 mb-2">Session Details:</div>
                <div className="text-white">
                  <div>⏰ 12-hour access</div>
                  <div>🎮 Ready to launch</div>
                  <div>💳 Payment confirmed</div>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <button
              onClick={handleLaunchGame}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-lg transition-colors"
            >
              🎮 Launch Game Now
            </button>
            
            <button
              onClick={handleGoToMyGames}
              className="w-full bg-gray-600 hover:bg-gray-700 text-white py-3 px-6 rounded-lg transition-colors"
            >
              📋 Go to My Games
            </button>
          </div>

          <div className="mt-6 text-xs text-gray-500">
            <p>Session ID: {sessionId}</p>
            <p>You can launch this game anytime within the next 12 hours</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LaunchSuccess;
